import { useState, useEffect } from 'react';
import AxiosInstance from 'src/components/Axios';

// Модульні кеші — живуть протягом сесії браузера.
// Скидаються вручну через invalidateCategoryScopes() або при перезавантаженні сторінки.
let cacheActive = null;
let cacheInactive = null;

/**
 * Скидає обидва кеші скоупів (активні та неактивні).
 * Викликати після успішного створення або редагування scope у SupportScopesForm,
 * щоб наступні виклики хуків зробили свіжі запити на сервер.
 */
export const invalidateCategoryScopes = () => {
	cacheActive = null;
	cacheInactive = null;
};

/**
 * Хук для отримання списку активних скоупів категорій (`/category-scopes?is_active=true`).
 *
 * Перший виклик робить HTTP-запит і зберігає результат у модульному кеші.
 * Усі наступні виклики (навіть у різних компонентах) повертають кешовані дані
 * без повторного запиту. Для примусового оновлення — викликати invalidateCategoryScopes()
 * перед наступним рендером компонента, що використовує цей хук.
 *
 * @returns {Array} Масив об'єктів активних скоупів. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const scopes = useCategoryScopes();
 * // [{ id: 1, code: 'login', name: 'Логін', is_active: true }, ...]
 */
export const useCategoryScopes = () => {
	const [scopes, setScopes] = useState(cacheActive || []);

	useEffect(() => {
		if (cacheActive) return;
		AxiosInstance.get('/category-scopes?is_active=true')
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheActive = list;
				setScopes(list);
			})
			.catch(() => {});
	}, []);

	return scopes;
};

/**
 * Хук для отримання списку неактивних скоупів категорій (`/category-scopes?is_active=false`).
 *
 * Поведінка кешування аналогічна useCategoryScopes — один запит на сесію.
 * Кеш спільно скидається через invalidateCategoryScopes().
 *
 * Використовується для відображення застарілих (відключених) скоупів,
 * що вже прив'язані до категорій.
 *
 * @returns {Array} Масив об'єктів неактивних скоупів. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const inactiveScopes = useInactiveCategoryScopes();
 * // [{ id: 4, code: 'test', name: 'тест', is_active: false }, ...]
 */
export const useInactiveCategoryScopes = () => {
	const [scopes, setScopes] = useState(cacheInactive || []);

	useEffect(() => {
		if (cacheInactive) return;
		AxiosInstance.get('/category-scopes?is_active=false')
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheInactive = list;
				setScopes(list);
			})
			.catch(() => {});
	}, []);

	return scopes;
};
